import React, { useEffect, useMemo, useState } from 'react'
import { Eye, Globe, MapPin, Trophy, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '../hooks/use-i18n'
import {
  getCountries,
  getCitiesByCountry,
  QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
} from '../services/publicService'
import roomsService, { type PublicFootballStatus, type PublicFormula1Status } from '../services/roomsService'
import type { CountryItem, CityItem } from '../types/public'
import { getLeagueDisplayLabel } from '../utils/leagueDisplayLabel'
import { LocationFieldButton, SelectPanel } from './SearchableSelect'

interface PlayWorldFromRoomModalProps {
  roomId: string
  status: PublicFootballStatus | PublicFormula1Status
  onClose: () => void
  onSuccess?: () => void
  variant?: 'football' | 'formula1'
}

const FEATURES = [
  { icon: Trophy, tone: 'text-amber-400 bg-amber-400/15', key: 'room.playWorld.modal.featureRankings' },
  { icon: Eye, tone: 'text-sky-400 bg-sky-400/15', key: 'room.playWorld.modal.featurePublic' },
  { icon: MapPin, tone: 'text-violet-400 bg-violet-400/15', key: 'room.playWorld.modal.featureScopes' },
] as const

const PlayWorldFromRoomModal: React.FC<PlayWorldFromRoomModalProps> = ({
  roomId,
  status,
  onClose,
  onSuccess,
  variant = 'football',
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'confirm' | 'location'>('confirm')
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [cities, setCities] = useState<CityItem[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [countryCode, setCountryCode] = useState('')
  const [city, setCity] = useState('')
  const [loading, setLoading] = useState(false)
  const [openPicker, setOpenPicker] = useState<'country' | 'city' | null>(null)
  const [pickerQuery, setPickerQuery] = useState('')

  useEffect(() => {
    setStep('confirm')
    setCountryCode('')
    setCity('')
  }, [roomId])

  const leaguesToJoin =
    'missingLeagues' in status
      ? (status.missingLeagues.length > 0 ? status.missingLeagues : status.roomLeagues)
      : ['formula1']

  const leagueLabels = useMemo(
    () => leaguesToJoin.map((l) => getLeagueDisplayLabel(l, t)).join(', '),
    [leaguesToJoin, t],
  )

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    if (!countryCode.trim()) {
      setCities([])
      setCity('')
      return
    }
    let cancelled = false
    setLoadingCities(true)
    setCity('')
    getCitiesByCountry(countryCode)
      .then((list) => { if (!cancelled) setCities(list) })
      .catch(() => { if (!cancelled) setCities([]) })
      .finally(() => { if (!cancelled) setLoadingCities(false) })
    return () => { cancelled = true }
  }, [countryCode])

  const handleJoin = async () => {
    if (step === 'confirm' && (status.needsLocation || !status.hasPublicParticipant)) {
      setStep('location')
      return
    }
    if (step === 'location' || status.needsLocation || !status.hasPublicParticipant) {
      if (!countryCode.trim()) {
        toast.error(t('playPublic.countryRequired'))
        return
      }
      if (!city.trim()) {
        toast.error(t('playPublic.cityRequired'))
        return
      }
    }

    setLoading(true)
    try {
      const result =
        variant === 'formula1'
          ? await roomsService.joinPublicFormula1FromRoom(roomId, {
              countryCode: countryCode || undefined,
              city: city || undefined,
              carryOverPredictions: true,
            })
          : await roomsService.joinPublicFootballFromRoom(roomId, {
              countryCode: countryCode || undefined,
              city: city || undefined,
              carryOverPredictions: true,
            })
      const count = result.syncedPredictionCount ?? 0
      toast.success(
        count > 0
          ? t(variant === 'formula1' ? 'room.playWorld.f1success' : 'room.playWorld.success', { count: String(count) })
          : t(variant === 'formula1' ? 'room.playWorld.f1successNone' : 'room.playWorld.successNone'),
      )
      onSuccess?.()
      onClose()
      await queryClient.refetchQueries({ queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS })
      navigate(
        variant === 'formula1'
          ? (result as { publicFormula1Url?: string }).publicFormula1Url || '/public/formula1'
          : result.publicFootballUrl || '/public/football',
      )
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t(variant === 'formula1' ? 'room.playWorld.failedF1' : 'room.playWorld.failed'))
    } finally {
      setLoading(false)
    }
  }

  const countryItems = countries.map((c) => ({ value: c.code, label: c.name }))
  const cityItems = cities.map((c) => ({ value: c.cityName, label: c.cityName }))
  const selectedCountryLabel = countries.find((c) => c.code === countryCode)?.name || ''

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/65 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full h-[min(36rem,calc(100vh-2rem))] max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {openPicker && step === 'location' ? (
          <SelectPanel
            title={openPicker === 'country' ? t('playPublic.country') : t('playPublic.city')}
            items={openPicker === 'country' ? countryItems : cityItems}
            value={openPicker === 'country' ? countryCode : city}
            query={pickerQuery}
            onQueryChange={setPickerQuery}
            placeholder={
              openPicker === 'country' ? t('playPublic.selectCountry') : t('playPublic.selectCity')
            }
            onBack={() => {
              setOpenPicker(null)
              setPickerQuery('')
            }}
            onSelect={(value) => {
              if (openPicker === 'country') setCountryCode(value)
              else setCity(value)
              setOpenPicker(null)
              setPickerQuery('')
            }}
          />
        ) : (
          <>
        <div className="relative px-5 pt-5 pb-4 border-b border-border/60 bg-gradient-to-b from-primary/10 to-transparent shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-primary/20 text-primary shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-foreground leading-tight">
                {t(variant === 'formula1' ? 'room.playWorld.modal.titleF1' : 'room.playWorld.modal.title')}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  variant === 'formula1' ? 'room.playWorld.modal.leadF1' : 'room.playWorld.modal.lead',
                  { leagues: leagueLabels },
                )}
              </p>
            </div>
          </div>
        </div>

              <div className="p-5 flex-1 min-h-0 overflow-y-auto">
          {step === 'confirm' ? (
            <>
              {leaguesToJoin.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {leaguesToJoin.map((league) => (
                    <span
                      key={league}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20"
                    >
                      {getLeagueDisplayLabel(league, t)}
                    </span>
                  ))}
                </div>
              )}

              <ul className="space-y-3 mb-4">
                {FEATURES.map(({ icon: Icon, tone, key }) => (
                  <li key={key} className="flex items-start gap-3">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-xl shrink-0 ${tone}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <p className="text-sm text-foreground pt-1.5 leading-snug">{t(key)}</p>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-muted-foreground mb-5">
                {t('room.playWorld.modal.optionalNote')}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                  {t('room.playWorld.modal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={loading}
                  className="flex-[1.4] px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {loading ? t('common.loading') : t('room.playWorld.modal.confirm')}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">{t('playPublic.step2Desc')}</p>
              <div className="space-y-3 mb-5">
                <LocationFieldButton
                  label={`${t('playPublic.country')} *`}
                  value={selectedCountryLabel}
                  placeholder={t('playPublic.selectCountry')}
                  onOpen={() => {
                    setPickerQuery('')
                    setOpenPicker('country')
                  }}
                />
                <LocationFieldButton
                  label={`${t('playPublic.city')} *`}
                  value={city}
                  placeholder={
                    loadingCities
                      ? t('common.loading')
                      : countryCode
                        ? t('playPublic.selectCity')
                        : t('playPublic.selectCountryFirst')
                  }
                  disabled={!countryCode || loadingCities}
                  onOpen={() => {
                    setPickerQuery('')
                    setOpenPicker('city')
                  }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                  {t('playPublic.back')}
                </button>
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={loading}
                  className="flex-[1.4] px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? t('common.loading') : t('room.playWorld.modal.confirm')}
                </button>
              </div>
            </>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PlayWorldFromRoomModal
